import { sortBy } from "lodash";
import { Select } from "components/atoms/Select";
import {
  generateSelectOptions,
  ISelectOption,
} from "components/atoms/Select/UseSelect";
import { Flag } from "components/atoms/Flag";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";

export type SalesAccountOption = Pick<
  ISalesAccount,
  | "id"
  | "uuid"
  | "status"
  | "salesAccountReference"
  | "tradeName"
  | "companyName"
  | "financialCompany"
>;

/**
 * The searchable sales account list of the header's customer switcher:
 * sorted by trade name, searchable by trade name, company name, reference or
 * id, each row with the financial company's flag.
 */
export const SalesAccountSelect = ({
  salesAccounts,
  value,
  onChange,
  placeholder = "Select customer",
  testId,
}: {
  salesAccounts: SalesAccountOption[];
  value: SalesAccountOption["id"] | null;
  onChange(account: ISelectOption<SalesAccountOption>): void;
  placeholder?: string;
  testId?: string;
}) => {
  const options = generateSelectOptions<SalesAccountOption>({
    data: sortBy<SalesAccountOption>(
      salesAccounts,
      [(customer) => customer?.tradeName?.toLowerCase()],
      "customerName"
    ),
    valueKey: "uuid",
    labelKey: "tradeName",
  });
  const selected =
    options.find((customer) => customer.id !== null && customer.id === value) ||
    null;

  return (
    <Select<ISelectOption<SalesAccountOption>>
      options={options}
      placeholder={placeholder}
      value={selected}
      testId={testId}
      customFilter={(option, query) => {
        const normalizedQuery = query.toLowerCase();

        return (
          (option.tradeName &&
            option.tradeName.toLowerCase().includes(normalizedQuery)) ||
          (option.companyName &&
            option.companyName.toLowerCase().includes(normalizedQuery)) ||
          (option.salesAccountReference &&
            option.salesAccountReference
              .toLowerCase()
              .includes(normalizedQuery)) ||
          (option.id !== null &&
            option.id !== undefined &&
            String(option.id).includes(query.trim()))
        );
      }}
      optionTemplate={({
        tradeName,
        companyName,
        salesAccountReference,
        financialCompany,
      }) => {
        const normalizedTradeName = (tradeName || "").trim().toLowerCase();
        const normalizedCompanyName = (companyName || "").trim().toLowerCase();
        const shouldShowCompanyName =
          !!normalizedCompanyName &&
          normalizedCompanyName !== normalizedTradeName;

        return (
          <div className="flex items-center">
            <Flag
              countryCode={
                financialCompany === 1
                  ? "GB"
                  : financialCompany === 8
                  ? "US"
                  : "NL"
              }
            />
            <div className="ml-2">
              {tradeName}
              {shouldShowCompanyName ? ` (${companyName})` : ""} –{" "}
              {salesAccountReference}
            </div>
          </div>
        );
      }}
      onChange={(option) => {
        if (option) onChange(option);
      }}
    />
  );
};
